import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../../config/axiosConfig';
import { API_URLS, TIMEZONES } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { ProfileChangePasswordModal, PrivacyPolicyModal, TermsAndConditionsModal } from '../../components/modals';

// Zod validation schema for profile (without password fields)
const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required').trim(),
  lastName: z.string().min(1, 'Last name is required').trim(),
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  phone: z.string().min(1, 'Phone number is required'),
  timezone: z.string().min(1, 'Timezone is required'),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const Profile: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Initialize form with session data if available
  const getInitialFormData = () => {
    const currentUser = sessionManager.getUser();
    if (currentUser) {
      const nameParts = currentUser.name?.split(' ') || ['', ''];
      return {
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: currentUser.email || '',
        phone: '',
        timezone: 'eastern',
      };
    }
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      timezone: 'eastern',
    };
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: getInitialFormData(),
  });

  const loadUserProfile = useCallback(async () => {
    try {
      setIsLoading(true);

      // Get current user profile using the dedicated endpoint
      const response = await api.get(API_URLS.GET_CURRENT_USER);

      if (response.data.success) {
        const user = response.data.data;

        if (user) {
          setUserProfile(user);
          // Pre-fill form with user data
          reset({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phone: user.phone || '',
            timezone: user.timezone || 'eastern',
          });
        } else {
          showErrorMsg('User profile not found.');
        }
      } else {
        showErrorMsg(response.data.message || 'Failed to load profile information.');
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      showErrorMsg('Failed to load profile information.');
    } finally {
      setIsLoading(false);
    }
  }, [reset]);

  // Load user profile on component mount
  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      setIsLoading(true);

      const updateData: any = {
        _id: userProfile._id,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        timezone: data.timezone,
      };

      const response = await api.put(API_URLS.UPDATE_USER, updateData);

      if (response.data.success) {
        showSuccessMsg('Profile updated successfully!');

        // Update session if email or name changed
        const currentSession = sessionManager.getSession();
        if (currentSession) {
          const updatedSession = {
            ...currentSession,
            user: {
              ...currentSession.user,
              email: data.email,
              name: `${data.firstName} ${data.lastName}`.trim(),
            },
          };
          sessionManager.saveSession(updatedSession);
        }

        // Reload profile to get updated data
        loadUserProfile();
      } else {
        showErrorMsg(response.data.message || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      if (error.response?.data?.message) {
        showErrorMsg(error.response.data.message);
      } else {
        showErrorMsg('Failed to update profile');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Get user role display text
  const getUserRoleText = () => {
    // Check if we have userProfile data with resolved role label
    if (userProfile && userProfile.roleLabel) {
      return userProfile.roleLabel; // Use the resolved role label from API
    }

    // Fallback to session data
    const currentUser = sessionManager.getUser();
    const role = currentUser?.role;
    return role?.label || 'User';
  };

  // Get user permissions display data
  // const getUserPermissions = () => {
  //   const permissions = userProfile?.permissions || {};
  //   return {
  //     editUsers: permissions.editUsers || false,
  //     editKpis: permissions.editKpis || false,
  //     editPlayers: permissions.editPlayers || false,
  //     editOpponents: permissions.editOpponents || false,
  //     editSeasons: permissions.editSeasons || false,
  //     editGames: permissions.editGames || false,
  //   };
  // };

  // Only show loading screen if we're loading and don't have any data at all
  if (isLoading && !userProfile && !sessionManager.getUser()) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="relative">
          <div
            className="absolute inset-0 rounded-full h-16 w-16 border-4 border-transparent border-r-emerald-400 animate-spin"
            style={{
              animationDirection: 'reverse',
              animationDuration: '1.5s',
            }}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h1 className="text-xl font-bold">Profile Settings</h1>
              <p className="text-green-100 text-base mt-1">Manage your personal information and account settings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white max-w-5xl mx-auto shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          {/* Profile Header with Action Buttons */}
          <div className="flex flex-col gap-2 md:flex-row justify-between items-center p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Profile Information</h2>
              <p className="text-gray-600 mt-1">Manage your personal information and account settings</p>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setIsEditing(!isEditing)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
              >
                {isEditing ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Cancel</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    <span>Edit Profile</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowPasswordModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <span>Change Password</span>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-8">
            {/* Personal Information Section */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
              {/* Two-row layout for form fields */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* First Row */}
                <div className="space-y-6">
                  <div className="group">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      <span>First Name *</span>
                    </label>
                    <input
                      type="text"
                      {...register('firstName')}
                      className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-green-500/20 group-hover:border-green-300 ${
                        errors.firstName
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:border-green-500'
                      } ${!isEditing ? 'bg-gray-50 text-gray-600' : ''}`}
                      placeholder="Enter your first name"
                      disabled={!isEditing || isLoading || isSubmitting}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{errors.firstName.message}</span>
                      </p>
                    )}
                  </div>

                  <div className="group">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                        />
                      </svg>
                      <span>Email Address *</span>
                    </label>
                    <input
                      type="email"
                      {...register('email')}
                      className="w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-green-500/20 group-hover:border-green-300 bg-gray-50 text-gray-600 border-gray-200"
                      placeholder="Enter your email address"
                      disabled={true}
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-2 flex items-center space-x-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Email address cannot be changed</span>
                    </p>
                  </div>

                  <div className="group">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>Timezone *</span>
                    </label>
                    <select
                      {...register('timezone')}
                      className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-green-500/20 group-hover:border-green-300 ${
                        errors.timezone
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:border-green-500'
                      } ${!isEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                      disabled={!isEditing || isLoading || isSubmitting}
                    >
                      {TIMEZONES.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                    {errors.timezone && (
                      <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{errors.timezone.message}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Second Row */}
                <div className="space-y-6">
                  <div className="group">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      <span>Last Name *</span>
                    </label>
                    <input
                      type="text"
                      {...register('lastName')}
                      className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-green-500/20 group-hover:border-green-300 ${
                        errors.lastName
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-gray-200 focus:border-green-500'
                      } ${!isEditing ? 'bg-gray-50 text-gray-600' : ''}`}
                      placeholder="Enter your last name"
                      disabled={!isEditing || isLoading || isSubmitting}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{errors.lastName.message}</span>
                      </p>
                    )}
                  </div>

                  <div className="group">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      <span>Phone Number *</span>
                    </label>
                    <input
                      type="tel"
                      {...register('phone')}
                      className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-green-500/20 group-hover:border-green-300 ${
                        errors.phone ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-green-500'
                      } ${!isEditing ? 'bg-gray-50 text-gray-600' : ''}`}
                      placeholder="Enter your phone number"
                      disabled={!isEditing || isLoading || isSubmitting}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{errors.phone.message}</span>
                      </p>
                    )}
                  </div>

                  {/* Role Information (Read-only) */}
                  <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-100">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                        />
                      </svg>
                      <span>Position/Role</span>
                    </label>
                    <input
                      type="text"
                      value={getUserRoleText()}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 font-medium"
                      disabled
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-2 flex items-center space-x-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Contact your administrator to change your role</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Permissions Section -show with permissions */}
              {/* <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <div className="mb-4">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <svg
                      className="w-4 h-4 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    <span>Permissions</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-4">
                    These are the actions you are authorized to perform in the
                    system
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(() => {
                    const permissions = getUserPermissions();
                    const permissionLabels = {
                      editUsers: "Edit Users",
                      editKpis: "Edit KPIs",
                      editPlayers: "Edit Players",
                      editOpponents: "Edit Opponents",
                      editSeasons: "Edit Seasons",
                      editGames: "Edit Games",
                    };

                    return Object.entries(permissions).map(
                      ([key, hasPermission]) => (
                        <div
                          key={key}
                          className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-all duration-200 ${
                            hasPermission
                              ? "bg-green-50 border-green-200 text-green-800"
                              : "bg-gray-50 border-gray-200 text-gray-500"
                          }`}
                        >
                          <div
                            className={`flex-shrink-0 ${
                              hasPermission ? "text-green-600" : "text-gray-400"
                            }`}
                          >
                            {hasPermission ? (
                              <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                          <span
                            className={`font-medium text-sm ${
                              hasPermission ? "text-green-800" : "text-gray-600"
                            }`}
                          >
                            {
                              permissionLabels[
                                key as keyof typeof permissionLabels
                              ]
                            }
                          </span>
                        </div>
                      )
                    );
                  })()}
                </div>

                <p className="text-xs text-gray-500 mt-4 flex items-center space-x-1">
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Contact your administrator to modify permissions</span>
                </p>
              </div> */}

              {/* Save Button - Only show when editing */}
              {isEditing && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isLoading || isSubmitting}
                      className="group relative bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-4 focus:ring-green-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                      {isLoading || isSubmitting ? (
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          <span>Saving Changes...</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Save Changes</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Legal Links Section */}
        <div className="mt-8 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Legal Information</h3>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-8">
              <button
                onClick={() => setShowPrivacyModal(true)}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                  />
                </svg>
                <span>Privacy Policy</span>
              </button>

              <button
                onClick={() => setShowTermsModal(true)}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-green-600 hover:text-green-700 bg-white border border-green-200 rounded-lg hover:bg-green-50 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <span>Terms & Conditions</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              By using STATDOG, you agree to our terms and privacy practices.
            </p>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ProfileChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onPasswordChanged={() => {
          // Optional: Reload profile or update session if needed
          loadUserProfile();
        }}
      />

      <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />

      <TermsAndConditionsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </div>
  );
};

export default Profile;
